// ─────────────────────────────────────────────────────────────────────────────
// Trending, the daily spike card. (trending 2026-07-06)
//
// Surfaces the worker's Trending items (GET /api/trending, ≥15% day-over-day
// spikes across per-post views, platform stats, and named Spotify tracks;
// 7-day TTL) as LARGE CLICKABLE PILLS: thumbnail (song artwork / post image /
// video cover proxy) + Ryan's headline copy + a "+23%" chip. Clicking a pill
// opens the detail modal with the recommendation copy and the external
// platform link (new tab). Empty state stays honest ("Nothing trending yet").
//
// Three exports:
//   • Trending          , self-fetching card for the desktop ARTIST dashboard
//                          (authHeaders carry X-Band-Id under impersonation).
//   • TrendingPillList  , bare pill list + modal state; reused by the roster
//                          TrendingCard (parts.tsx) and anywhere else.
//   • TrendingDetailModal, the portaled modal (desktop detail view).
//
// Money-neutral: spikes aren't financial, so no canSeeMoney gate (Bible 06.5
// untouched). Data is band-scoped by the worker (Bible 06.4) and fail-empty.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchTrendingItems,
  trendingHeadline,
  trendingDetail,
  trendingPctLabel,
  trendingWhenLabel,
  trendingPlatformLabel,
} from '../../lib';
import type { TrendingItem } from '../../lib';
import { TrendSparkline } from '../../ui'; /* trend-viz 2026-07-06 */
import './trending.css';

// Per-platform tone for the letter-tile fallback when an item has no thumb.
const PLATFORM_TONE: Record<string, string> = {
  instagram: '#b13589',
  tiktok: '#0e0c0b',
  facebook: '#1877f2',
  youtube: '#cd201f',
  spotify: '#1db954',
  threads: '#0e0c0b',
};

/** Thumb: real artwork/cover when we have it, letter tile fallback otherwise.
    A broken image URL (expired CDN link etc.) falls back to the tile too. */
function TrendThumb({ item }: { readonly item: TrendingItem }): JSX.Element {
  const [broken, setBroken] = useState(false);
  const tone = PLATFORM_TONE[String(item.platform || '').toLowerCase()] || '#8B1A2B';
  const letter = (trendingPlatformLabel(item.platform) || '?').charAt(0);
  if (!item.thumb || broken) {
    return (
      <span className="trnd-pill__thumb trnd-pill__thumb--tile" style={{ ['--tone' as string]: tone }}>
        {letter}
      </span>
    );
  }
  return (
    <img
      className="trnd-pill__thumb"
      src={item.thumb}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

/** The detail modal (desktop). Portaled to <body> (same reason as
    OpportunityModal, transformed ancestors can't reparent the overlay). */
export function TrendingDetailModal({
  item,
  onClose,
}: {
  readonly item: TrendingItem;
  readonly onClose: () => void;
}): JSX.Element {
  const d = trendingDetail(item);

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="trnd-overlay">
      <div className="trnd-backdrop" onClick={onClose} />
      <div className="trnd-modal" role="dialog" aria-label="Trending detail">
        <button className="trnd-close" type="button" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="trnd-modal__head">
          <TrendThumb item={item} />
          <div>
            <p className="trnd-modal__eyebrow">
              {trendingPlatformLabel(item.platform)} · {trendingWhenLabel(item)}
            </p>
            <h3 className="trnd-modal__title">{d.heading}</h3>
          </div>
          <span className="trnd-pct trnd-pct--lg">{trendingPctLabel(item)}</span>
        </div>
        <p className="trnd-modal__body">{d.body}</p>
        {/* trend-viz 2026-07-06, the 14 daily values the sweep's detection
            actually saw, spike day emphasized. Items without a captured
            series (pre-capture detections) render no graph, the wrapper is
            conditional so no phantom margin appears either. */}
        {item.series && item.series.length >= 2 ? (
          <div style={{ margin: '14px 0 2px' }}>
            <TrendSparkline series={item.series} />
          </div>
        ) : null}
        <div className="trnd-modal__actions">
          {d.linkUrl && (
            /* External platform link, always a NEW TAB on desktop. */
            <a className="trnd-link" href={d.linkUrl} target="_blank" rel="noopener noreferrer">
              {d.linkLabel || 'Open link'} ↗
            </a>
          )}
          <button className="trnd-dismiss" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Bare pill list + modal open state. Reused by the roster card. `wide` lays the
    pills out in a responsive 2-up grid for the dashboard's wide main-column slot
    (where Trending replaced Biggest movers 2026-07-07); the default single-column
    flow is unchanged for the narrow rail + roster card. */
export function TrendingPillList({
  items,
  wide,
}: {
  readonly items: ReadonlyArray<TrendingItem>;
  readonly wide?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState<TrendingItem | null>(null);
  return (
    <div className={'trnd-list' + (wide ? ' trnd-list--wide' : '')}>
      {items.map((it) => (
        <button
          className="trnd-pill"
          type="button"
          key={it.subject || it.id}
          onClick={() => setOpen(it)}
        >
          <TrendThumb item={it} />
          <span className="trnd-pill__b">
            <span className="trnd-pill__t">{trendingHeadline(it)}</span>
            <span className="trnd-pill__m">
              {trendingPlatformLabel(it.platform)} · {trendingWhenLabel(it)}
            </span>
          </span>
          <span className="trnd-pct">{trendingPctLabel(it)}</span>
        </button>
      ))}
      {open && <TrendingDetailModal item={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Self-fetching Trending card for the desktop artist Dashboard.
    `bandId` is a refetch key only (the JEDI/impersonation switchers change
    it), the actual scoping rides authHeaders' X-Band-Id. `wide` renders the
    card in the wide main-column slot (2026-07-07: Trending replaced Biggest
    movers there) with a 2-up pill grid; omit it for the narrow rail. */
export function Trending({ bandId, wide }: { readonly bandId?: string; readonly wide?: boolean }): JSX.Element {
  const [items, setItems] = useState<ReadonlyArray<TrendingItem>>([]);
  useEffect(() => {
    let alive = true;
    // Band switch → drop the previous band's pills IMMEDIATELY (no carry-over,
    // Bible 06.4·C), then revalidate.
    setItems([]);
    void fetchTrendingItems().then((list) => {
      if (alive) setItems(list);
    });
    return () => {
      alive = false;
    };
  }, [bandId]);

  return (
    <section className={'card' + (wide ? ' trending-card--wide' : '')} id="trending-card">
      <header className="card__head">
        <h3 className="card__title">Trending</h3>
        <span className="tag">{items.length}</span>
      </header>
      <div className="card__body" style={{ paddingTop: 8 }}>
        {items.length === 0 ? (
          // Honest empty state, the agreed shell copy, never filler.
          <div className="trnd-empty">
            <p className="trnd-empty__t">Nothing trending yet</p>
            <p className="trnd-empty__s">
              Notable streaming, social, and UGC movements show up here once connected.
            </p>
          </div>
        ) : (
          <TrendingPillList items={items} wide={wide} />
        )}
      </div>
    </section>
  );
}
